// frontend/src/components/Profile/UploadDocumentModal.js
import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, TextField,
  CircularProgress, Alert, Paper, alpha, useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const API_BASE = 'https://fincollab.ru/api/auth';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(3),
    maxWidth: 500,
    width: '90%',
    backgroundColor: theme.palette.grey[50]
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const DropzoneArea = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  border: '2px dashed',
  borderColor: theme.palette.divider,
  borderRadius: theme.spacing(3),
  transition: 'all 0.2s',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.02)
  }
}));

const FilePreview = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: '1px solid',
  borderColor: theme.palette.divider,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
}));

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const UploadDocumentModal = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError('Размер файла не должен превышать 10 МБ');
      return;
    }

    setSelectedFile(file);
    setError('');

    if (!title) {
      setTitle(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError('Размер файла не должен превышать 10 МБ');
      return;
    }

    setSelectedFile(file);
    setError('');

    if (!title) {
      setTitle(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !title.trim()) {
      setError('Укажите название и выберите файл');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('file', selectedFile);

      const response = await fetch(`${API_BASE}/my-documents/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          handleReset();
        }, 1000);
      } else {
        const data = await response.json();
        setError(data.error || data.detail || 'Ошибка при загрузке');
      }
    } catch (err) {
      console.error('❌ Ошибка загрузки документа:', err);
      setError('Ошибка соединения с сервером');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setTitle('');
    setSelectedFile(null);
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <UploadIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight="700">
            Загрузка документа
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent sx={{ p: 3, pt: 5 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
            Документ успешно загружен!
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Название документа"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mt: 2, mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}
          placeholder="Например: Сертификат участника конференции"
        />

        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.zip,.rar"
        />

        {!selectedFile ? (
          <DropzoneArea
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Перетащите файл сюда
            </Typography>
            <Typography variant="body2" color="text.secondary">
              или кликните для выбора (PDF, DOC, изображения, макс. 10 МБ)
            </Typography>
          </DropzoneArea>
        ) : (
          <FilePreview>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FileIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="body2" fontWeight="600">
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatSize(selectedFile.size)}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setSelectedFile(null)} size="small" color="error">
              <DeleteIcon />
            </IconButton>
          </FilePreview>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 5, px: 4 }}>
          Отмена
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={uploading || success || !selectedFile || !title.trim()}
          startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          sx={{ borderRadius: 5, px: 4 }}
        >
          {uploading ? 'Загрузка...' : 'Загрузить'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default UploadDocumentModal;